import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { stylesClasses, generateKey } from '../../utils/classes'
import { responsiveStyles } from '../../utils/styles'
import { createUseStyles } from 'react-jss'
import mediaQueries from '../../constants/mediaQueries'

import './Container.scss'

const SPACING_VALUES = {
	0: 0,
	1: 0.125,
	2: 0.25,
	3: 0.5,
	4: 0.75,
	5: 1,
	6: 1.25,
	7: 1.5,
	8: 2,
	9: 2.5,
	10: 3,
	11: 4,
	12: 5,
	13: 6.25,
	14: 7.5,
	15: 9
}

/**
 * Returns object that stored, for each mediaQuery, desired key and its asociated CSS, for all sizes.
 * @param {string} mq - MediaQuery name.
 * @return {object} - All pairs {key: value}, where value is the CSS.
 */
function generatePaddingStyles(mq) {
	const spacingPairs = new Map(Object.entries(SPACING_VALUES))
	let styles = {}

	spacingPairs.forEach((value, key) => {
		const spacingVal = key
		const spacingScalar = value
		const gutterSize = 1.5 / 2

		const mqKey = generateKey('container--pad-x-', spacingVal, mq)
		const cssVal = `${gutterSize + (1 * spacingScalar)}rem`
		styles[mqKey] = responsiveStyles(mq, {
			paddingLeft: cssVal,
			paddingRight: cssVal
		})


	})

	return styles
}

const styles = {
	...Object.keys(mediaQueries).reduce(
		(styles, mq) => ({
			...styles,
			...generatePaddingStyles(mq),
		}),
		{}
	)
}
const useStyles = createUseStyles(styles, { name: 'Vb' })

const Container = forwardRef((props, ref) => {
	const {
		className, // This prop is defined here to prevent its use. It's not allowed to override the container element.
		component: Component = 'div',
		disableGutters = false,
		paddingX,
		breakpoint = 'xl',
		children,
		...other
	} = props

	const classes = useStyles(props)

	return (
		<Component
			ref={ref}
			className={clsx(
				breakpoint ? `Vb-container--${breakpoint}` : 'Vb-container',
				disableGutters && 'Vb-container--no-gutters',
				paddingX && !disableGutters && stylesClasses(paddingX, 'container--pad-x-', classes),
			)}
			{...other}
		>
			{children}
		</Component>
	)
})

Container.displayName = 'Container'

export default Container

Container.defaultProps = {
	component: 'div',
	disableGutters: false,
	breakpoint: 'xl'
}


/**
 * Functions to validate if props are within the allowed
 * range of types and values, as so the mediaqueries are too.
 */
const isSpacing = (props, propName, componentName) => {
	const value = props[propName]
	const spacingPairs = new Map(Object.entries(SPACING_VALUES))
	const spacingValues = [...spacingPairs.keys()]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(
						mediaQueries
					).join(', ')}`
				)
				return false
			}
			if (!(value[key] in spacingValues)) {
				error = new Error(
					`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(
						', '
					)}`
				)
				return false
			}
			return true
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'undefined') {
		return null
	} else if (typeof value === 'number') {
		return value in spacingValues // TO IMPROVE -> take keys from SPACING_VALUES as numbers for the prop comparison. Object and Map return keys as strings.
			? null
			: new Error(
				`${propName} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(', ')}`
			)
	}

	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: ${spacingValues.join(', ')}`)
}

Container.propTypes = {
	/**
	 * The content of the container.
	 */
	children: PropTypes.node,

	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * * @default 'div'
	 */
	component: PropTypes.elementType,

	/**
	 * If `true`, the left and right padding is removed.
	 * @default false
	 */
	disableGutters: PropTypes.bool,

	/**
	 * Determine the max-width of the container.
	 * The container width grows with the size of the screen.
	 * Set to `false` to disable `maxWidth`.
	 * @default 'xl'
	 */
	breakpoint: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'fluid']),
	/**
	 * The pading of the container, by mediaQuery, in addition to the gutter.
	 * One of the allowed spacing values
	 * @default null
	 */
	paddingX: isSpacing
}
